// Default bodyweight and mobility workouts: no gear, or only a pull-up bar and a band. Our own plans.
import type { Workout } from '../types';
import { ids, reps, t } from './plan';

const CHALLENGE_SOURCE = { label: 'Our own, in the style of the named challenge workouts in apps like Freeletics' };

export const BODYWEIGHT_WORKOUTS: Workout[] = [
  {
    id: 'beginner_15',
    name: 'Beginner 15',
    description: 'A first session for anyone: squats, knee push-ups, bridges and a short plank, three easy sets each.',
    level: 'beginner',
    focus: 'full_body',
    equipment: ['bodyweight'],
    xp: 30,
    source: { label: 'Our own' },
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('main', [
        { exerciseId: 'squat', amount: reps(10) },
        { exerciseId: 'knee_push_up', amount: reps(8) },
        { exerciseId: 'glute_bridge', amount: reps(12) },
        { exerciseId: 'plank', amount: t(20) },
      ]) },
    ],
  },
  {
    id: 'core_15',
    name: 'Core 15',
    description: 'Front, sides and back of the core: five moves, three rounds, short rests.',
    level: 'beginner',
    focus: 'core',
    equipment: ['bodyweight'],
    xp: 35,
    source: { label: 'Our own' },
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 3, workSec: 40, restSec: 10 }, blocks: ids('main', [
        { exerciseId: 'plank', amount: t(40) },
        { exerciseId: 'dead_bug', amount: reps(10, { perSide: true }) },
        { exerciseId: 'bicycle_crunch', amount: reps(20) },
        { exerciseId: 'side_plank', amount: t(30, true) },
        { exerciseId: 'mountain_climber', amount: t(30) },
      ]) },
    ],
  },
  {
    id: 'tabata_core',
    name: 'Tabata Core',
    description: 'Tabata timing, 20 seconds hard and 10 seconds rest, for eight rounds of four core moves.',
    level: 'intermediate',
    focus: 'core',
    equipment: ['bodyweight'],
    xp: 40,
    source: { label: 'Our own; the 20/10 timing follows the Tabata interval protocol' },
    warmupId: 'mobility',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 8, workSec: 20, restSec: 10 }, blocks: ids('main', [
        { exerciseId: 'mountain_climber' },
        { exerciseId: 'bicycle_crunch' },
        { exerciseId: 'plank_jacks' },
        { exerciseId: 'flutter_kicks' },
      ]) },
    ],
  },
  {
    id: 'hotel_room_20',
    name: 'Hotel Room 20',
    description: 'Quiet, no jumping, fits next to a bed: a full-body circuit for when you travel.',
    level: 'beginner',
    focus: 'full_body',
    equipment: ['bodyweight'],
    xp: 45,
    source: { label: 'Our own' },
    warmupId: 'general',
    cooldownId: 'breathing',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 3, workSec: 40, restSec: 20 }, blocks: ids('main', [
        { exerciseId: 'squat' },
        { exerciseId: 'push_up' },
        { exerciseId: 'reverse_lunge', note: 'alternate legs' },
        { exerciseId: 'plank_to_push_up' },
        { exerciseId: 'glute_bridge' },
      ]) },
    ],
  },
  {
    id: 'pull_up_progression',
    name: 'Pull-up Progression',
    description: 'From no pull-up to the first one and beyond: hangs, shoulder-blade pulls, slow negatives and band help. Twice a week.',
    level: 'beginner',
    focus: 'upper_body',
    equipment: ['pullup_bar', 'band'],
    xp: 40,
    source: { label: 'Our own' },
    warmupId: 'upper_body',
    cooldownId: 'upper_back',
    sections: [
      { id: 'hang', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('hang', [
        { exerciseId: 'dead_hang', amount: t(20) },
        { exerciseId: 'scapular_pull_up', amount: reps(8) },
      ]) },
      { id: 'pull', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 90 }, blocks: ids('pull', [
        { exerciseId: 'negative_pull_up', amount: reps(3), note: '3 to 5 seconds down' },
        { exerciseId: 'band_assisted_pull_up', amount: reps(5, { max: 8 }) },
        { exerciseId: 'inverted_row', amount: reps(8) },
      ]) },
    ],
  },
  {
    id: 'comet',
    name: 'Comet',
    description: 'A 15-minute challenge: burpees, push-ups, squats and sit-ups, as many rounds as you can. Note your rounds and beat them next time.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['bodyweight'],
    xp: 50,
    source: CHALLENGE_SOURCE,
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'amrap', minutes: 15 }, blocks: ids('main', [
        { exerciseId: 'burpee', amount: reps(5) },
        { exerciseId: 'push_up', amount: reps(10) },
        { exerciseId: 'squat', amount: reps(15) },
        { exerciseId: 'sit_up', amount: reps(10) },
      ]) },
    ],
  },
  {
    id: 'granite',
    name: 'Granite',
    description: 'Five hard rounds of jumps, push-ups and holds with a minute of rest between them. Legs and chest will know.',
    level: 'advanced',
    focus: 'full_body',
    equipment: ['bodyweight'],
    xp: 60,
    source: CHALLENGE_SOURCE,
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      // Rep and time amounts on every block, no rest between them: the circuit's workSec is not used here.
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 5, workSec: 30, restSec: 0, roundRestSec: 60 }, blocks: ids('main', [
        { exerciseId: 'squat_jump', amount: reps(10) },
        { exerciseId: 'push_up', amount: reps(10) },
        { exerciseId: 'lunge_jump', amount: reps(5, { perSide: true }) },
        { exerciseId: 'mountain_climber', amount: t(30) },
        { exerciseId: 'hollow_hold', amount: t(30) },
      ]) },
    ],
  },
  {
    id: 'nova',
    name: 'Nova',
    description: 'Twelve minutes of fast intervals, 30 seconds on and 15 off. Sweat first, think later.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['bodyweight'],
    xp: 40,
    source: CHALLENGE_SOURCE,
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 30, restSec: 15 }, blocks: ids('main', [
        { exerciseId: 'high_knees' },
        { exerciseId: 'burpee' },
        { exerciseId: 'skaters' },
        { exerciseId: 'plank_jacks' },
      ]) },
    ],
  },
  {
    id: 'morning_stretch_10',
    name: 'Morning Stretch 10',
    description: 'Ten gentle minutes to wake up the spine, hips and shoulders. No equipment, no sweat.',
    level: 'beginner',
    focus: 'mobility',
    equipment: ['none'],
    xp: 15,
    source: { label: 'Our own' },
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'list' }, blocks: ids('main', [
        { exerciseId: 'cat_cow', amount: t(45) },
        { exerciseId: 'childs_pose', amount: t(45) },
        { exerciseId: 'cobra_stretch', amount: t(30) },
        { exerciseId: 'downward_dog', amount: t(45) },
        { exerciseId: 'worlds_greatest_stretch', amount: t(30, true) },
        { exerciseId: 'deep_squat_hold', amount: t(45) },
        { exerciseId: 'standing_forward_fold', amount: t(45) },
        { exerciseId: 'torso_twists', amount: t(45) },
        { exerciseId: 'side_bend_stretch', amount: t(30, true) },
        { exerciseId: 'shoulder_rolls', amount: t(30) },
        { exerciseId: 'neck_side_stretch', amount: t(20, true) },
        { exerciseId: 'box_breathing', amount: t(60) },
      ]) },
    ],
  },
  {
    id: 'mobility_15',
    name: 'Mobility 15',
    description: 'Joint by joint, from neck to ankles: open the upper back, the hips and the ankles. Good on rest days.',
    level: 'beginner',
    focus: 'mobility',
    equipment: ['none'],
    xp: 20,
    source: { label: 'Our own' },
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'list' }, blocks: ids('main', [
        { exerciseId: 'neck_rolls', amount: t(45) },
        { exerciseId: 'shoulder_rolls', amount: t(45) },
        { exerciseId: 'wall_slides', amount: t(60) },
        { exerciseId: 'thoracic_open_book', amount: t(45, true) },
        { exerciseId: 'thread_the_needle', amount: t(30, true) },
        { exerciseId: 'cat_cow', amount: t(60) },
        { exerciseId: 'ninety_ninety_switch', amount: t(60) },
        { exerciseId: 'worlds_greatest_stretch', amount: t(45, true) },
        { exerciseId: 'frog_stretch', amount: t(60) },
        { exerciseId: 'couch_stretch', amount: t(45, true) },
        { exerciseId: 'deep_squat_hold', amount: t(60) },
        { exerciseId: 'ankle_rocks', amount: t(30, true) },
        { exerciseId: 'belly_breathing', amount: t(60) },
      ]) },
    ],
  },
  {
    id: 'desk_break_5',
    name: 'Desk Break 5',
    description: 'Five minutes between meetings: neck, chest, hips and back, all standing.',
    level: 'beginner',
    focus: 'mobility',
    equipment: ['none'],
    xp: 10,
    source: { label: 'Our own' },
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'list' }, blocks: ids('main', [
        { exerciseId: 'neck_side_stretch', amount: t(20, true) },
        { exerciseId: 'shoulder_rolls', amount: t(30) },
        { exerciseId: 'doorway_chest_stretch', amount: t(30, true) },
        { exerciseId: 'torso_twists', amount: t(30) },
        { exerciseId: 'standing_quad_stretch', amount: t(20, true) },
        { exerciseId: 'standing_forward_fold', amount: t(30) },
        { exerciseId: 'deep_breathing', amount: t(30) },
      ]) },
    ],
  },
];
